import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { createBlockedTime } from "../services/bookingService";
import { formatTime } from "../services/datetime";

/**
 * Take time out of the diary: a stocktake, a team meeting, a machine being
 * serviced.
 *
 * Naming a provider takes that one person off the floor; naming nobody closes
 * the branch for the stretch.
 *
 * A block over existing bookings is **allowed** — the API returns what it
 * displaces rather than refusing. The business already knows it is doing this;
 * what it needs is the list of people to ring, which is shown here before the
 * dialog closes.
 */
export default function BlockTimeModal({ open, branchId, providers = [], date, onClose, onBlocked }) {
  const { t } = useTranslation();

  const [staffId, setStaffId] = useState("");
  const [day, setDay] = useState("");
  const [from, setFrom] = useState("09:00");
  const [to, setTo] = useState("10:00");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [displaces, setDisplaces] = useState(null);

  useEffect(() => {
    if (!open) return;

    setStaffId("");
    setDay(date ?? "");
    setFrom("09:00");
    setTo("10:00");
    setReason("");
    setError("");
    setFieldErrors({});
    setDisplaces(null);
  }, [open, date]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setFieldErrors({});

    try {
      const { displaces: clashes } = await createBlockedTime(branchId, {
        staffId: staffId || null,
        startsAt: `${day} ${from}`,
        endsAt: `${day} ${to}`,
        reason,
      });

      // Bookings were displaced: show them rather than closing silently, so
      // whoever did this leaves with the list of people to contact.
      if (clashes.length > 0) {
        setDisplaces(clashes);
        onBlocked?.(clashes);
        return;
      }

      onBlocked?.([]);
      onClose();
    } catch (err) {
      setFieldErrors(apiFieldErrors(err));
      setError(apiErrorMessage(err, t("diary.blockFailed")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="card w-full max-w-lg p-5">
        <h2 className="mb-4 text-lg font-bold">{t("diary.blockTitle")}</h2>

        {displaces ? (
          <div>
            <div className="rounded-lg border border-warning-line bg-warning-wash px-3 py-2 text-sm text-warning-text">
              {t("diary.displacedWarning", { count: displaces.length })}
            </div>

            <ul className="mt-3 max-h-48 divide-y divide-line overflow-y-auto rounded-lg border border-line">
              {displaces.map((booking) => (
                <li key={booking.id} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span>
                    <span className="font-medium">{booking.client ?? t("diary.noClient")}</span>
                    <span className="ms-2 text-xs text-ink-2" dir="ltr">
                      {booking.reference}
                    </span>
                  </span>
                  <span className="text-xs text-ink-2" dir="ltr">
                    {formatTime(booking.local_starts_at)}
                  </span>
                </li>
              ))}
            </ul>

            <div className="mt-4 flex justify-end">
              <button type="button" className="btn-primary" onClick={onClose}>
                {t("common.close")}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="field-label" htmlFor="block-staff">
                {t("diary.blockWho")}
              </label>
              <select
                id="block-staff"
                className="field-input"
                value={staffId}
                onChange={(e) => setStaffId(e.target.value)}
              >
                <option value="">{t("diary.wholeBranch")}</option>
                {providers.map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {provider.display_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <label className="field-label" htmlFor="block-date">
                  {t("diary.date")}
                </label>
                <input
                  id="block-date"
                  type="date"
                  className="field-input"
                  value={day}
                  onChange={(e) => setDay(e.target.value)}
                />
              </div>
              <div>
                <label className="field-label" htmlFor="block-from">
                  {t("diary.from")}
                </label>
                <input
                  id="block-from"
                  type="time"
                  className="field-input"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </div>
              <div>
                <label className="field-label" htmlFor="block-to">
                  {t("diary.to")}
                </label>
                <input
                  id="block-to"
                  type="time"
                  className="field-input"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
                {fieldErrors.ends_at && <p className="field-error">{fieldErrors.ends_at[0]}</p>}
              </div>
            </div>

            <div>
              <label className="field-label" htmlFor="block-reason">
                {t("diary.reason")}
              </label>
              <input
                id="block-reason"
                className="field-input"
                placeholder={t("diary.reasonPlaceholder")}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>

            {error && <p className="field-error">{error}</p>}

            <div className="flex justify-end gap-2 pt-1">
              <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
                {t("common.cancel")}
              </button>
              <button type="submit" className="btn-primary" disabled={saving || !day}>
                {saving ? t("common.saving") : t("diary.block")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
